/**
 * Ksting
 *
 * Copyright © 2017 Chris Andersson <chris@wbisbi.com>. All rights reserved.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

import React, { Component } from 'react';
import { object, shape, func, string } from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';

import p from 'polyglot/polyglot';

import { createUrl } from 'utils/urlHelper';

import {
  CLIENT,
  MODEL,
  PHOTOGRAPHER,
  CLOTHING_STYLIST,
  HEAD_STYLIST,
  INFLUENCER,
} from 'actions/signup';

import {
  DELETE_USER_SUCCESS,
  addRole as addRoleAction,
  deleteUser as deleteUserAction,
} from 'actions/admin';

import { updateProfile as updateProfileAction } from 'actions/user';

import Button from 'components/Button/Button';

import EditStatus from './EditStatus/EditStatus';
import AddRoles from './AddRoles/AddRoles';
import EditTags from './EditTags/EditTags';
import CreateAgentRole from './CreateAgentRole/CreateAgentRole';

import styles from './admin.scss';

/**
 * This is the Admin component.
 *
 * @author Chris Andersson <chris@wbisbi.com>
 */
export class Admin extends Component {
  /**
   * Declare expected prop types.
   *
   * @type {object}
   */
  static propTypes = {
    history: object.isRequired,
    user: object.isRequired,
    auth: shape({
      token: string.isRequired,
    }).isRequired,
    bootstrap: shape({
      constants: object.isRequired,
      languages: object.isRequired,
    }).isRequired,
    addRole: func.isRequired,
    updateProfile: func.isRequired,
    deleteUser: func.isRequired,
  };

  onClickDelete = event => {
    event.preventDefault();

    const {
      auth: { token },
      history,
      user: { user_id },
      deleteUser,
    } = this.props;

    if (window.confirm(p.t('general.confirm_delete'))) {
      deleteUser(token, { user_id }).then(({ type }) => {
        if (type === DELETE_USER_SUCCESS) {
          history.push(createUrl('home'));
        }
      });
    }
  };

  onUpdateProfile = profile => {
    const {
      auth: { token },
      updateProfile,
    } = this.props;

    updateProfile(token, profile);
  };

  onAddRole = (role_type, agency_id = null) => {
    const {
      auth: { token },
      user: { user_id },
      addRole,
    } = this.props;

    if (role_type === 'AGENT') {
      addRole(token, {
        user_id,
        role_type,
        agency_id,
      });
    } else {
      addRole(token, {
        user_id,
        role_type,
      });
    }
  };

  /**
   * Render Admin.
   *
   * @return {Object}
   */
  render() {
    const {
      user,
      bootstrap: {
        constants: {
          agencies,
          head_rates,
          head_talent_types,
          model_rates,
          model_talent_types,
          photographer_rates,
          photographer_talent_types,
          stylist_rates,
          stylist_talent_types,
        },
      },
    } = this.props;

    const {
      is_accepted,
      is_client,
      is_agent,
      is_clothing_stylist,
      is_head_stylist,
      is_influencer,
      is_model,
      is_photographer,
      show_edit_mode,
    } = user;

    const roles = [
      {
        type: CLIENT,
        active: is_client,
      },
      {
        type: MODEL,
        active: is_model,
        rates: model_rates,
        talentTypes: model_talent_types,
      },
      {
        type: HEAD_STYLIST,
        active: is_head_stylist,
        rates: head_rates,
        talentTypes: head_talent_types,
      },
      {
        type: PHOTOGRAPHER,
        active: is_photographer,
        rates: photographer_rates,
        talentTypes: photographer_talent_types,
      },
      {
        type: INFLUENCER,
        active: is_influencer,
      },
      {
        type: CLOTHING_STYLIST,
        active: is_clothing_stylist,
        rates: stylist_rates,
        talentTypes: stylist_talent_types,
      },
    ];

    return (
      <article className={styles.row}>
        {!is_accepted && (
          <div className={styles.content}>
            <EditStatus roles={roles} />
          </div>
        )}

        {is_accepted && (
          <div className={styles.content}>
            <AddRoles roles={roles} addRole={this.onAddRole} />
            {is_client && !is_agent && (
              <CreateAgentRole
                user={user}
                options={agencies}
                onSubmit={this.onAddRole}
              />
            )}
            {roles
              .filter(
                ({ active, rates, talentTypes }) =>
                  active && rates && talentTypes
              )
              .map(role => (
                <EditTags
                  key={role.type}
                  user={user}
                  onSubmit={this.onUpdateProfile}
                  {...role}
                />
              ))}
          </div>
        )}

        {show_edit_mode && (
          <div className={styles.content}>
            <h2 className={styles.title}>
              {p.t('account.admin.delete_user.title')}
            </h2>
            <Button small dark ghost onClick={this.onClickDelete}>
              {p.t('account.admin.delete_user.button_text')}
            </Button>
          </div>
        )}
      </article>
    );
  }
}

export default withRouter(
  connect(({ bootstrap, auth, user }) => ({ bootstrap, auth, user }), {
    updateProfile: updateProfileAction,
    deleteUser: deleteUserAction,
    addRole: addRoleAction,
  })(Admin)
);
